// Push Notification Service for OneView
// Handles browser push notifications for incidents

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.checkPermission();
  }

  checkPermission() {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  canNotify() {
    return 'Notification' in window && this.permission === 'granted';
  }

  notify(title, options = {}) {
    if (!this.canNotify()) {
      console.warn('Notifications not permitted');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      tag: 'oneview-notification',
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  notifyIncident(incident) {
    const severityLabels = {
      5: 'DISASTER',
      4: 'HIGH',
      3: 'AVERAGE',
      2: 'WARNING',
      1: 'INFO'
    };

    const title = `[${severityLabels[incident.severity] || 'ALERT'}] ${incident.host_name}`;
    
    return this.notify(title, {
      body: incident.name,
      tag: `incident-${incident.id}`,
      requireInteraction: incident.severity >= 4,
      data: { incidentId: incident.id },
      onClick: () => {
        window.location.href = '/dashboard/incidents';
      }
    });
  }

  // Play sound alert
  playSound(type = 'default') {
    const sounds = {
      default: '/sounds/notification.mp3',
      alert: '/sounds/alert.mp3',
      critical: '/sounds/critical.mp3'
    };

    try {
      const audio = new Audio(sounds[type] || sounds.default);
      audio.volume = 0.5;
      audio.play().catch(err => {
        // Autoplay may be blocked by browser
        console.warn('Could not play sound:', err);
      });
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }
}

// Singleton instance
export const notificationService = new NotificationService();

// React hook for notifications
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useNotifications(getAuthHeader) {
  const [settings, setSettings] = useState({
    push_enabled: true,
    sound_enabled: false,
    sound_type: 'default',
    min_severity: 4
  });
  const previousIncidentsRef = useRef(new Set());
  const [enabled, setEnabled] = useState(false);

  // Load notification settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/notification-settings`, {
          headers: getAuthHeader()
        });
        setSettings(response.data);
        
        if (response.data.push_enabled) {
          const granted = await notificationService.requestPermission();
          setEnabled(granted);
        }
      } catch (error) {
        console.error('Failed to load notification settings:', error);
      }
    };
    
    if (getAuthHeader) {
      loadSettings();
    }
  }, [getAuthHeader]);

  // Check for new incidents and notify
  const checkNewIncidents = useCallback((incidents) => {
    if (!enabled || !settings.push_enabled) return;

    const currentIds = new Set(incidents.map(i => i.id));
    const previous = previousIncidentsRef.current;

    // Find new incidents
    incidents.forEach(incident => {
      if (!previous.has(incident.id) &&
          previous.size > 0 && // Don't notify on initial load
          incident.severity >= settings.min_severity) {

        // Show notification
        notificationService.notifyIncident(incident);

        // Play sound if enabled
        if (settings.sound_enabled) {
          notificationService.playSound(settings.sound_type);
        }
      }
    });

    previousIncidentsRef.current = currentIds;
  }, [enabled, settings]);

  return {
    settings,
    enabled,
    checkNewIncidents,
    requestPermission: () => notificationService.requestPermission()
  };
}
